import { AnalysisSubject, type AnalysisSubjectType, logExhaustive, useApi } from 'venn-utils';
import { type AnalysisViewTypeEnum, getFund, getSpecificPortfolioV3, viewEntity } from 'venn-api';
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { proxySetIdState } from 'venn-state';

/**
 * useFetchAbortableSubject is a hook that fetches the analysis subject based on the provided objectId and objectType.
 * As this is abortable, it is CRITICAL that this be used only for one single subject at a time.
 * Unlike Studio, in Analysis a subject can be a primary, benchmark or secondary subject and the
 * fact that this is abortable means that navigating away or changing subjects directly on the page
 * can trigger an abort race condition that can break loading the next page.
 * @param savedId the saveId to be used for tracking the viewId
 */
export const useFetchAbortableSubject = (savedId: string | undefined) => {
  const abortableGetSpecificPortfolio = useApi(getSpecificPortfolioV3);
  const abortableGetFund = useApi(getFund);
  const proxySetId = useRecoilValue(proxySetIdState);

  // There are three cases we will call fetchSubjectApi:
  // (1) when the page first load
  // (2) when we use navigation to change analysis portfolio to a new one
  // (3) when the user change benchmark or proxy or other metadata for investment.
  // ReloadingAllocationPanel is for the #2 case, we want to reload the allocation panel.
  // So the allocator panel won't be at the `changed` status.
  const fetchSubjectApi = useCallback(
    async (
      objectId: number | string | undefined,
      objectType: AnalysisSubjectType | undefined,
      redirectToAnalysis: () => void,
      strategyIdIn?: number,
    ): Promise<
      | {
          subject: AnalysisSubject;
        }
      | undefined
    > => {
      if (!objectId || !objectType) {
        redirectToAnalysis();
        return undefined;
      }

      if (objectType === 'private-investment' || objectType === 'private-portfolio') {
        return undefined;
      }

      const savedInfo = savedId
        ? {
            viewId: savedId,
            viewType: 'ANALYSIS' as AnalysisViewTypeEnum,
          }
        : undefined;
      try {
        if (objectType === 'investment') {
          const fundResponse = await abortableGetFund(objectId as string, proxySetId);
          if (!fundResponse) {
            return undefined;
          }
          // Track when users analyse investments
          viewEntity(objectId as string, savedInfo);
          return {
            subject: new AnalysisSubject(fundResponse.content, 'investment', proxySetId),
          };
        }
        if (objectType === 'portfolio') {
          const portfolioResponse = await abortableGetSpecificPortfolio(
            Number(objectId),
            undefined /* version - required for abortcontroller */,
            proxySetId,
          );
          if (!portfolioResponse) {
            return undefined;
          }

          const strategyId = portfolioResponse.content.historical ? undefined : strategyIdIn;
          let subject = new AnalysisSubject(portfolioResponse.content, 'portfolio', proxySetId, { strategyId });
          // Check if selected strategy is actually a fund, and if so, fetch the fund, too
          if (subject.isInvestmentInPortfolio) {
            try {
              const selectedFundResponse = await abortableGetFund(subject.strategy!.fund!.id, proxySetId);
              if (!selectedFundResponse) {
                return undefined;
              }
              subject = new AnalysisSubject(subject.superItem, subject.superType, subject.proxySetId, {
                strategyId,
                strategyFund: selectedFundResponse.content,
              });
              // Track when users analyse investments in portfolio
              viewEntity(subject.strategy!.fund!.id, savedInfo);
            } catch (e) {
              // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
              if (e.name !== 'AbortError') {
                // Fetching the fund failed. Don't let this fund be selected. Instead, select the root.
                subject = new AnalysisSubject(subject.superItem, subject.superType, subject.proxySetId);
              }
            }
          }
          // Track root portfolio Id
          viewEntity(subject.superId.toString(), savedInfo);
          return {
            subject,
          };
        }

        logExhaustive(objectType, 'objectType expected to be exhaustively handled');
        redirectToAnalysis();
        return undefined;
      } catch (e) {
        // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
        if (e.name !== 'AbortError') {
          redirectToAnalysis();
        }
        return undefined;
      }
    },
    [savedId, abortableGetSpecificPortfolio, abortableGetFund, proxySetId],
  );

  return fetchSubjectApi;
};
